import * as React from 'react';
import {
  Button,
  Input,
  Select,
  makeStyles,
  tokens,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Title3,
  Text,
  Badge,
  mergeClasses,
} from '@fluentui/react-components';
import { Add20Regular as AddIcon, Delete20Regular as DeleteIcon, ReOrderDotsVertical20Regular as GripIcon } from '@fluentui/react-icons';
import { EventoEstrutura } from '../../../types/event';
import { getSeverityBadgeColor, getSeverityText } from '../../../utils/severityHelpers';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: tokens.spacingVerticalL,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    // Ocultar tabela em mobile
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  tableRow: {
    backgroundColor: tokens.colorNeutralBackground1,
    transition: 'all 0.2s ease',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow4,
    },
  },
  // Layout de cards para mobile
  mobileCards: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
    },
  },
  card: {
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    transition: 'all 0.2s ease',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
  },
  cardId: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground2,
  },
  cardTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    flex: 1,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalS,
  },
  cardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase200,
  },
  cardLabel: {
    color: tokens.colorNeutralForeground2,
  },
  cardValue: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightMedium,
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXS,
  },
  actionsColumn: {
    width: '8%',
    textAlign: 'center',
    padding: tokens.spacingHorizontalM,
    verticalAlign: 'middle',
  },
  idColumn: {
    width: '6%',
    textAlign: 'center',
    padding: tokens.spacingHorizontalM,
    verticalAlign: 'middle',
  },
  detalheColumn: {
    width: '32%',
    padding: tokens.spacingHorizontalM,
    verticalAlign: 'middle',
  },
  severidadeColumn: {
    width: '14%',
    padding: tokens.spacingHorizontalM,
    verticalAlign: 'middle',
  },
  pontuacaoColumn: {
    width: '12%',
    padding: tokens.spacingHorizontalM,
    verticalAlign: 'middle',
  },
  regraComparacaoColumn: {
    width: '12%',
    padding: tokens.spacingHorizontalM,
    verticalAlign: 'middle',
  },
  metaColumn: {
    width: '10%',
    padding: tokens.spacingHorizontalM,
    verticalAlign: 'middle',
  },
  dragHandleColumn: {
    width: '6%',
    textAlign: 'center',
    cursor: 'grab',
    padding: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    verticalAlign: 'middle',
    '&:hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  dragging: {
    opacity: 0.6,
    transform: 'scale(1.02) rotate(1deg)',
    boxShadow: tokens.shadow28,
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  dropTarget: {
    backgroundColor: tokens.colorBrandBackground2,
    borderTop: `3px solid ${tokens.colorBrandForeground1}`,
    transform: 'scale(1.01)',
  },
  emptyState: {
    textAlign: 'center',
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXL}`,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
  },
  idBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  inputField: {
    width: '100%',
  },
  selectField: {
    width: '100%',
    minWidth: '120px',
  },
});

interface EventStructuresGridProps {
  estruturas: EventoEstrutura[];
  onAddEstrutura: () => void;
  onRemoveEstrutura: (index: number) => void;
  onUpdateEstrutura: (index: number, field: keyof EventoEstrutura, value: any) => void;
  onReorderEstrutura: (fromIndex: number, toIndex: number) => void;
}

const EventStructuresGrid: React.FC<EventStructuresGridProps> = ({
  estruturas,
  onAddEstrutura,
  onRemoveEstrutura,
  onUpdateEstrutura,
  onReorderEstrutura,
}) => {
  const styles = useStyles();
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = React.useState<number | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const dragTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup do timeout quando o componente desmontar
  React.useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Prevenir drag se o usuário está interagindo com um input
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' ||
        target.closest('input') || target.closest('select') || target.closest('textarea')) {
      e.preventDefault();
      return;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());

    // Pequeno delay antes de mostrar o feedback visual para evitar conflitos
    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(true);
    }, 50);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    if (draggedIndex !== null && draggedIndex !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent, index: number) => {
    if (dropTargetIndex === index) {
      setDropTargetIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const draggedIndexStr = e.dataTransfer.getData('text/plain');
    const draggedIdx = parseInt(draggedIndexStr);

    if (!isNaN(draggedIdx) && draggedIdx !== dropIndex) {
      onReorderEstrutura(draggedIdx, dropIndex);
    }

    setDraggedIndex(null);
    setDropTargetIndex(null);
    setIsDragging(false);

    // Limpar timeout se existir
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
  };

  const getSeverityColor = (severity: number) => {
    return getSeverityBadgeColor(severity as 0 | 1 | 2 | 3);
  };

  const getSeverityLabel = (severity: number) => {
    return getSeverityText(severity as 0 | 1 | 2 | 3);
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Title3>Estruturas do Evento</Title3>
        <Button
          appearance="primary"
          size="small"
          icon={<AddIcon />}
          onClick={onAddEstrutura}
        >
          Adicionar Estrutura
        </Button>
      </div>

      {estruturas.length > 0 && (
        <>
          {/* Tabela para desktop */}
          <Table
            className={styles.table}
            onDragOver={handleDragOver}
            onDrop={(e) => {
              // Se não há dropTargetIndex, significa que o drop foi fora de uma linha específica
              // Nesse caso, não fazemos nada
              if (dropTargetIndex !== null) {
                handleDrop(e, dropTargetIndex);
              } else {
                // Mesmo se não houve drop em uma linha específica, resetar o estado
                setDraggedIndex(null);
                setDropTargetIndex(null);
                setIsDragging(false);
                // Limpar timeout se existir
                if (dragTimeoutRef.current) {
                  clearTimeout(dragTimeoutRef.current);
                  dragTimeoutRef.current = null;
                }
              }
            }}
          >
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.dragHandleColumn}></TableHeaderCell>
                <TableHeaderCell className={styles.idColumn}>ID</TableHeaderCell>
                <TableHeaderCell className={styles.detalheColumn}>Detalhe</TableHeaderCell>
                <TableHeaderCell className={styles.severidadeColumn}>Severidade</TableHeaderCell>
                <TableHeaderCell className={styles.pontuacaoColumn}>Pontuação</TableHeaderCell>
                <TableHeaderCell className={styles.regraComparacaoColumn}>Regra de Comparação</TableHeaderCell>
                <TableHeaderCell className={styles.metaColumn}>Meta</TableHeaderCell>
                <TableHeaderCell className={styles.actionsColumn}>Ações</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {estruturas.map((estrutura, index) => {
                const rowClassName = mergeClasses(
                  styles.tableRow,
                  isDragging && draggedIndex === index && styles.dragging,
                  dropTargetIndex === index && styles.dropTarget
                );
                
                return (
                <TableRow
                  key={index}
                  className={rowClassName}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragLeave={(e) => handleDragLeave(e, index)}
                >
                  <TableCell className={styles.dragHandleColumn}>
                    <div
                      style={{ display: 'flex', justifyContent: 'center', cursor: 'grab' }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                    >
                      <GripIcon style={{ fontSize: '20px' }} />
                    </div>
                  </TableCell>
                  <TableCell className={styles.idColumn}>
                    <div className={styles.idBadge}>{index + 1}</div>
                  </TableCell>
                  <TableCell className={styles.detalheColumn}>
                    <Input
                      size="medium"
                      value={estrutura.detalhe}
                      onChange={(e) => onUpdateEstrutura(index, 'detalhe', e.target.value)}
                      placeholder="Detalhe da estrutura"
                      maxLength={100}
                      className={styles.inputField}
                    />
                  </TableCell>
                  <TableCell className={styles.severidadeColumn}>
                    <Select
                      size="medium"
                      value={estrutura.severidade.toString()}
                      onChange={(e, data) => onUpdateEstrutura(index, 'severidade', parseInt(data.value || '0'))}
                      className={styles.selectField}
                    >
                      <option value="0">Baixa</option>
                      <option value="1">Média</option>
                      <option value="2">Alta</option>
                      <option value="3">Crítica</option>
                    </Select>
                  </TableCell>
                  <TableCell className={styles.pontuacaoColumn}>
                    <Input
                      size="medium"
                      type="number"
                      value={estrutura.pontuacao.toString()}
                      onChange={(e) => onUpdateEstrutura(index, 'pontuacao', parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className={styles.inputField}
                    />
                  </TableCell>
                  <TableCell className={styles.regraComparacaoColumn}>
                    <Select
                      size="medium"
                      value={estrutura.regraComparacao.toString()}
                      onChange={(e, data) => onUpdateEstrutura(index, 'regraComparacao', parseInt(data.value || '1'))}
                      className={styles.selectField}
                    >
                      <option value="1">Maior</option>
                      <option value="2">Menor</option>
                      <option value="3">Igual</option>
                    </Select>
                  </TableCell>
                  <TableCell className={styles.metaColumn}>
                    <Input
                      size="medium"
                      type="number"
                      step="0.01"
                      min="0"
                      max="999.99"
                      value={estrutura.meta.toString()}
                      onChange={(e) => onUpdateEstrutura(index, 'meta', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className={styles.inputField}
                    />
                  </TableCell>
                  <TableCell className={styles.actionsColumn}>
                    <Button
                      appearance="subtle"
                      size="medium"
                      icon={<DeleteIcon />}
                      onClick={() => onRemoveEstrutura(index)}
                      title="Remover estrutura"
                    />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Cards para mobile */}
          <div
            className={styles.mobileCards}
            onDragOver={handleDragOver}
            onDrop={(e) => {
              // Se não há dropTargetIndex, significa que o drop foi fora de um card específico
              // Nesse caso, não fazemos nada
              if (dropTargetIndex !== null) {
                handleDrop(e, dropTargetIndex);
              } else {
                // Mesmo se não houve drop em um card específico, resetar o estado
                setDraggedIndex(null);
                setDropTargetIndex(null);
                setIsDragging(false);
                // Limpar timeout se existir
                if (dragTimeoutRef.current) {
                  clearTimeout(dragTimeoutRef.current);
                  dragTimeoutRef.current = null;
                }
              }
            }}
          >
            {estruturas.map((estrutura, index) => {
              const cardClassName = mergeClasses(
                styles.card,
                isDragging && draggedIndex === index && styles.dragging,
                dropTargetIndex === index && styles.dropTarget
              );
              
              return (
              <div
                key={index}
                className={cardClassName}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragLeave={(e) => handleDragLeave(e, index)}
              >
                <div className={styles.cardHeader}>
                  <div
                    style={{ display: 'flex', alignItems: 'center' }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                  >
                    <GripIcon />
                  </div>
                  <div className={styles.cardId}>#{index + 1}</div>
                  <Badge
                    appearance="filled"
                    color={getSeverityColor(estrutura.severidade)}
                  >
                    {getSeverityLabel(estrutura.severidade)}
                  </Badge>
                  <div className={styles.cardActions}>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteIcon />}
                      onClick={() => onRemoveEstrutura(index)}
                      title="Remover estrutura"
                    />
                  </div>
                </div>

                <div className={styles.cardContent}>
                  <Input
                    size="small"
                    value={estrutura.detalhe}
                    onChange={(e) => onUpdateEstrutura(index, 'detalhe', e.target.value)}
                    placeholder="Detalhe da estrutura"
                    maxLength={100}
                    style={{ width: '100%', marginBottom: tokens.spacingVerticalS }}
                  />

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Severidade:</span>
                    <Select
                      size="small"
                      value={estrutura.severidade.toString()}
                      onChange={(e, data) => onUpdateEstrutura(index, 'severidade', parseInt(data.value || '0'))}
                      style={{ minWidth: '100px' }}
                    >
                      <option value="0">Baixa</option>
                      <option value="1">Média</option>
                      <option value="2">Alta</option>
                      <option value="3">Crítica</option>
                    </Select>
                  </div>

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Pontuação:</span>
                    <Input
                      size="small"
                      type="number"
                      value={estrutura.pontuacao.toString()}
                      onChange={(e) => onUpdateEstrutura(index, 'pontuacao', parseInt(e.target.value) || 0)}
                      placeholder="0"
                      style={{ width: '80px' }}
                    />
                  </div>

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Regra Comparação:</span>
                    <Select
                      size="small"
                      value={estrutura.regraComparacao.toString()}
                      onChange={(e, data) => onUpdateEstrutura(index, 'regraComparacao', parseInt(data.value || '1'))}
                      style={{ minWidth: '120px' }}
                    >
                      <option value="1">Maior</option>
                      <option value="2">Menor</option>
                      <option value="3">Igual</option>
                    </Select>
                  </div>

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Meta:</span>
                    <Input
                      size="small"
                      type="number"
                      step="0.01"
                      min="0"
                      max="999.99"
                      value={estrutura.meta.toString()}
                      onChange={(e) => onUpdateEstrutura(index, 'meta', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      style={{ width: '100px' }}
                    />
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}

      {estruturas.length === 0 && (
        <div className={styles.emptyState}>
          <AddIcon style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM, opacity: 0.5 }} />
          <Title3 style={{ marginBottom: tokens.spacingVerticalS }}>Nenhuma estrutura adicionada</Title3>
          <Text>Clique em "Adicionar Estrutura" para começar a configurar as estruturas do evento.</Text>
        </div>
      )}
    </div>
  );
};

export default EventStructuresGrid;
